import type { AskProposalQuestionResponse, ProposalAnswerViewModel, ProposalQuestionViewState } from '@/types/backend-contracts';

function formatPages(source: {
  pageStart?: number;
  pageEnd?: number;
}): string | undefined {
  if (typeof source.pageStart !== 'number') {
    return undefined;
  }

  if (typeof source.pageEnd === 'number' && source.pageEnd !== source.pageStart) {
    return `páginas ${source.pageStart}–${source.pageEnd}`;
  }

  return `página ${source.pageStart}`;
}

function formatSourceLabel(source: {
  publisher: string;
  documentTitle: string;
  pageStart?: number;
  pageEnd?: number;
}): string {
  const pages = formatPages(source);

  if (pages) {
    return `${source.documentTitle} — ${pages}`;
  }

  if (source.documentTitle) {
    return source.documentTitle;
  }

  if (source.publisher) {
    return source.publisher;
  }

  return 'Fonte oficial';
}

function buildSourceUrl(source: { url: string; pageStart?: number }): string {
  if (typeof source.pageStart !== 'number' || !Number.isFinite(source.pageStart) || source.pageStart <= 0) {
    return source.url;
  }

  const [baseUrl = '', hash = ''] = source.url.split('#', 2);
  const separator = baseUrl.includes('?') ? '&' : '?';
  const page = Math.trunc(source.pageStart);

  return `${baseUrl}${separator}page=${page}${hash ? `#${hash}` : ''}`;
}

export function toProposalAnswerViewModel(response: Extract<AskProposalQuestionResponse, { status: 'answered' }>): ProposalAnswerViewModel {
  return {
    answerId: response.answerId,
    summary: response.answer.summary,
    cards: response.answer.items.map((item) => ({
      title: item.title,
      description: item.description,
      sources: item.proposals.map((proposal) => ({
        label: formatSourceLabel(proposal.source),
        url: buildSourceUrl(proposal.source),
        pages: formatPages(proposal.source),
      })),
    })),
    limitations: response.answer.limitations,
  };
}

export function toProposalQuestionViewState(
  response: AskProposalQuestionResponse,
): ProposalQuestionViewState {
  if (response.status === 'needs-clarification') {
    return {
      status: 'needs-clarification',
      message: response.message,
    };
  }

  if (response.answer.items.length === 0) {
    return {
      status: 'no-proposals',
      message: response.answer.summary,
    };
  }

  return {
    status: 'answered',
    answer: toProposalAnswerViewModel(response),
  };
}
